import { BUTTONS } from '@/messages/buttons'
import { CONFIRMATION_MODALS, EXPIRATION_MODAL, INTERRUPT_MODALS } from '@/messages/modals'
import {
  ERROR_MODAL_MESSAGES,
  ERROR_NOTIFICATIONS,
  FORM_ERRORS,
  GENERAL_ALERTS,
  GENERAL_ERRORS,
} from '@/messages/errors'
import { TITLES } from '@/messages/titles'
import {
  CHECKOUT_FORM,
  CHECKOUT_MESSAGES,
  COUPON_MODAL,
  CUSTOMER_INFO_FORM,
} from '@/messages/checkout'
import { CART_SUMMARY, TICKET_SELECTION, TIMER } from '@/messages/tickets'
import { EVENTS_PAGE, SITE_CHROME } from '@/messages/site'
import { EVENTS_A11Y, TICKETING_A11Y } from '@/messages/a11y'
import { DAY_PASS_NAME } from '@/messages/constants'

export const TXT = {
  // Generic
  mw: 'Meow Wolf',
  time: 'time',
  date: 'date',

  // Modals
  confirmationModals: CONFIRMATION_MODALS,
  expirationModal: EXPIRATION_MODAL,
  interruptModals: INTERRUPT_MODALS,

  // Buttons
  buttons: BUTTONS,

  // Errors
  errorNotifications: ERROR_NOTIFICATIONS,
  errors: GENERAL_ERRORS,
  formErrors: FORM_ERRORS,
  alerts: GENERAL_ALERTS,
  errorModalMessages: ERROR_MODAL_MESSAGES,

  // Titles
  titles: TITLES,

  // Checkout
  customerInfoForm: CUSTOMER_INFO_FORM,
  checkoutForm: CHECKOUT_FORM,
  couponModal: COUPON_MODAL,
  checkoutMessages: CHECKOUT_MESSAGES,

  // Tickets and cart
  ticketSelection: TICKET_SELECTION,
  timer: TIMER,
  cartSummary: CART_SUMMARY,

  // Site chrome and events pages
  siteChrome: SITE_CHROME,
  eventsPage: EVENTS_PAGE,

  // Screen reader text
  a11y: TICKETING_A11Y,
  eventsA11y: EVENTS_A11Y,

  // Steps
  // --> Time Select
  timeSelect: {
    timezoneNotice: (zone: string) => `All hours are in ${zone}`,
    openHours: (open: string, close: string, date: string) => (
      `<span>Open ${open} to ${close}</span> on <span>${date}</span>`
    ),
    description: (dayPassTitle = DAY_PASS_NAME) => [
      'Select your arrival time',
      '<span style="text-transform:uppercase">or</span>',
      `choose ${dayPassTitle} to show up any time.`,
    ].join(' '),
    onSaleNotice: 'Tickets on sale 90 days in advance',
    selectDate: 'Select a date to continue.',
  },
  dateTimeHeader: {
    date: 'Date',
    entryTime: 'Entry Time',
    exhibitHours: 'Exhibit Hours',
  },
  calendarKeys: {
    stateAppreciation: { tip: (stateCode: StateCode) => `${stateCode} Appreciation day` },
    annualPassBlackoutDate: { tip: 'Annual Pass blockout day', description: 'blockout' },
    specialEvents: { tip: 'Special Events', description: 'special event' },
    soldOut: { tip: 'Sold out', description: 'sold out' },
    locals: 'Local day',
  },

  // --> Order Tickets
  orderItem: {
    showDescription: (title: string) => `Show details about ${title}`,
    hideDescription: (title: string) => `Hide details about ${title}`,
  },
  localChecker: {
    enterBillingZipSubtitle: 'Enter your billing zip code to unlock local pricing.',
    fieldLabel: 'Enter zip code',
  },

  // --> Add-Ons
  addOns: { header: 'Highly recommended, but you will love your visit to the multi-verse either way!' },

  // --> Customer Info
  customerInfo: {
    accountInformation: 'Account Information',
    guestCheckout: 'Guest Checkout',
    haveAnAccount: 'Have an Account?',
    loginPrompt: 'Login or create an account to access benefits and easier order changes!',
    logout: 'Logout',
  },
  forms: {
    giftCheckbox: 'Check box if this is a gift',
    giftIneligible: 'Annual Portal Pass tickets and perk tickets are not gift-able.',
  },

  // --> Checkout
  checkout: {
    lastStepCopy: 'Enter your payment info below.',
    // TODO: remove afterpay clearpay after migrating to affirm
    afterpayRequiresShippingAddress: 'Afterpay requires a shipping address and city.',
    affirmRequiresShippingAddress: 'Affirm requires a shipping address, city, and state.',
  },
  terms: {
    consent: 'I understand that tickets are nonrefundable and non-transferable across Meow Wolf locations.',
    preTitle: 'By clicking this box, I confirm I have read and agree to the',
    termsLinkText: 'Terms and Conditions',
    faqLinkText: 'FAQs',
  },

  // --> Purchase Confirmation
  purchaseConfirmation: {
    allSet: 'You are all set!',
    enjoy: 'Enjoy your visit to',
    orderNo: 'Order #',
    printTickets: 'Print Tickets',
    needHelp: 'Need help?',
    cardInfo: {
      paidOn: 'Paid on',
      paymentMethod: 'Payment Method:',
      stars: '****',
    },
    sentTo: (isGiftOrder: boolean) => `Your tickets have been ${isGiftOrder ? 'gifted' : 'sent'} to`,
    qrCode: {
      info: 'We emailed your ticket with a QR code.',
      infoBold: 'You do not have to print your tickets.',
    },
    sentToText: 'Your tickets have been sent to',
    giftedToText: 'Your tickets have been gifted to',
    waiver: {
      heading: 'Don’t forget to sign your waivers!',
      body: 'You can sign for yourself and dependents now, or share the link for other people in your party.',
      signNow: 'Sign waivers now',
      share: 'Share',
      shareTitle: 'Sign your waiver',
      shareText: (exhibitionName: string) => (exhibitionName
        ? `Please sign the waiver before your visit to ${exhibitionName}.`
        : 'Please sign the waiver before your visit.'),
    },
  },

  // Generic Steps
  steps: {
    noneSelected: 'None selected',
    skip: 'Skip this step',
  },

  // Info Area (Seller & Event description)
  infoArea: {
    priceText: 'Starting at',
    banners: { locals: 'Locals' },
  },

  // OrderSummaryHeader

  // Sticky Mobile Order Footer
  stickyMobileOrderFooter: {
    viewCartButtonLabel: 'View Order Summary',
    hideCartButtonLabel: 'Hide Order Summary',
  },

  // CouponInput.tsx
  couponCode: {
    name: 'couponCode',
    label: 'Enter coupon code',
    prefix: 'coupon-code',
    placeholder: 'Coupon code',
    errorMessage: 'Invalid coupon code',
    successMessage: 'Code Accepted',
  },
  giftCard: {
    name: 'giftCard',
    label: 'Enter gift card number',
    prefix: 'gift-card',
    placeholder: 'Gift Card Number',
    successMessage: 'Gift card applied',
    errors: {
      default: 'Error processing gift card',
      invalid: 'Please enter a valid gift card',
      emptyBalance: 'Please check card balance',
      duplicate: 'Card number already entered',
    },
  },

  // OrderSummary
  orderSummary: {
    dateTimeEmpty: '--',
    selectATime: 'Select a time from the options to the left',
    codeRequired: 'Code Required: ',
    code: 'Code',
    dateTimeDayPass: 'All Day',
    applied: 'Applied',
    annualPassNumber: (passNumber: string) => `<strong>Pass #:</strong> ${passNumber}`,
  },
  lineItemsSection: { items: 'Items' },
  subtotalsList: {
    subtotal: 'Subtotal',
    processingFee: 'Processing Fee',
    remove: 'Remove',
    remainingBalance: 'Remaining balance',
    total: 'Total',
    tax: 'Tax',
  },

  // Footer
  footer: {
    mwAllRightsReserved: 'Meow Wolf, All Rights Reserved',
    yourPrivacyChoices: 'Your Privacy Choices',
    termsOfUse: 'Terms of Use',
    privacyPolicy: 'Privacy Policy',
  },

  // EVENTS
  events: {
    eventAge: 'Age:',
    eventAllAges: 'All Ages',
    eventSeated: 'Seated Event',
  },
  emailNewsletter: {
    // this is a long line
    description: 'Subscribe for 15% off online Meow Wolf merch, behind-the-scenes secrets, and the latest news.',
  },

  // Other Pages
  pages: {
    // --> Error Page (404)
    error: {
      header: 'You\'ve found an empty portal!',
      text: 'There is nothing here apparently.',
    },
    // --> Confirmation load failure (purchase already completed)
    confirmationError: {
      header: 'We couldn\'t load your confirmation',
      text: 'Your order may have gone through. Please check your email for your tickets. '
        + 'If you don\'t see them shortly, contact us at help@meowwolf.com',
    },
    // --> Landing page, when the sellers can't be loaded
    landing: {
      sitesError: {
        header: 'We couldn\'t load our locations.',
        text: 'Please refresh the page or try again in a few minutes.',
      },
    },
  },
  tracking: {
    paymentRequest: {
      label: (orgName: string, cityName: string) => (
        `Admission Tickets ${orgName} ${cityName}`
      ),
    },
  },
  timeslots: {
    pricingUnavailable: 'Pricing unavailable',
    ticketsStartingAt: (price: string) => `Starting at ${price}`,
  },
  elapsedEvent: 'Past Event',
  adminVisibilityOnly: 'Admin Visibility Only',
  defaultEventDescription: 'Enjoy music at Meow Wolf, our stages are portals to new sounds and transcendent, interconnected, celestial, metagalactic vibes.',

}

export default TXT
